import React, { Fragment } from 'react';
import { useRouter } from 'next/router';
import { graphql, useRelayEnvironment } from 'react-relay';
import { useFetchQuery } from '@sparknz/journeys-common';
import {
  Box,
  ChoiceBox,
  ChoiceboxGroup,
  Text,
} from '@sparknz/spark-pattern-library';
import { LinkRegion, trackCustomLink } from '@sparknz/tracking';

import { ExistingPlansChangePlanOfferDetailsQuery } from '__generated__/ExistingPlansChangePlanOfferDetailsQuery.graphql';
import { useProductDetails } from 'journey/productDetails/provider';
import { ProductDetailsActionTypes } from 'journey/productDetails/reducer/productDetails.reducer';
import {
  EligibleLines,
  IfpEligibilityCheckSteps,
  IneligibleLines,
  IneligibleLinesReason,
  PlanBundleOptions,
  PlanOfferDetails,
} from 'journey/productDetails/typings';
import {
  getLineNumber,
  getUnsupportedLinesForChangePlan,
} from 'journey/productDetails/utils/getLineNumber.utils';
import { useCartCookie } from 'shared/hooks';
import { extractFirstValue } from 'shared/utils';
import ChangeAccountButton from '../../../ChangeAccountButton';
import { useTrackExistingPlanSelectorActionLayer } from '../../hooks';
import IneligibleWarning from '../IneligibleWarning';

type MergedLines = ((EligibleLines[0] | IneligibleLines[0]) & {
  isEligible: boolean;
  reason?: IneligibleLinesReason;
})[];

const UNSUPPORTED_CATEGORIES_FOR_BUY_NEW_PLAN = ['Accessory'];

const existingChangePlanOffersQuery = graphql`
  query ExistingPlansChangePlanOfferDetailsQuery(
    $lineNumber: String!
    $ifpOfferId: String
    $deviceOfferId: String
    $cartId: String
  ) {
    me {
      line(lineNumber: $lineNumber) {
        planProductOffers(
          ifpOfferId: $ifpOfferId
          deviceOfferId: $deviceOfferId
          cartId: $cartId
        ) {
          offerDetails {
            id
            name
            category {
              id
            }
            contentKey
            contractTerm {
              name
            }
            description
            price {
              basePrice
              basePriceExcludingTax
              effectivePrice
              effectivePriceExcludingTax
              frequency {
                period
                value
              }
              priceType
            }
            images {
              thumbnailUrl
            }
            entitlements {
              contentKey
              iconUrl
              longDescription
              name
              shortDescription
              sortOrder
              type
            }
            promotions {
              contentKey
              iconUrl
              longDescription
              name
              productType
              shortDescription
              sortOrder
            }
            productCharacteristics {
              balanceManagement
              isEligibilityRequired
            }
            dataLayerCategory
          }
        }
      }
    }
  }
`;

const BUY_NEW_SPARK_PLAN = 'Buy a new Spark plan';

interface ExistingPlanProps {
  selectedLineNumber: string;
  setSelectedLineNumber: (lineNumber: string) => void;
}

const ExistingPlans = ({
  selectedLineNumber,
  setSelectedLineNumber,
}: ExistingPlanProps) => {
  const router = useRouter();
  const {
    selectedDevice,
    paymentOptions,
    ifpEligibility: {
      eligibility: { accounts, eligibleLines, ineligibleLines },
    },
    dispatch,
  } = useProductDetails();
  const relayEnvironment = useRelayEnvironment();
  const { cartId } = useCartCookie();
  const { fetch: fetchQuery } =
    useFetchQuery<ExistingPlansChangePlanOfferDetailsQuery>(
      existingChangePlanOffersQuery,
      relayEnvironment,
    );
  const ifpId = extractFirstValue(router.query.ifpId);
  const deviceOfferId = extractFirstValue(router.query.offerId);

  useTrackExistingPlanSelectorActionLayer({
    numberOfEligiblePlansForIFP: eligibleLines.length,
    device: selectedDevice,
    paymentOption: paymentOptions?.find(({ id }) => id === ifpId),
  });

  const setLoader = (status: boolean) => {
    dispatch({
      type: ProductDetailsActionTypes.SetIfpEligibility,
      ifpEligibility: {
        activeStep: IfpEligibilityCheckSteps.LineSelection,
        isChangePlanOfferDetailsLoading: status,
      },
    });
  };

  const setOfferDetails = (offerDetails: PlanOfferDetails) => {
    dispatch({
      type: ProductDetailsActionTypes.SelectChangePlan,
      changePlanOffers: offerDetails,
    });
  };

  const getUpdatedPlanOfferDetails = async (lineNumber: string) => {
    setLoader(true);
    await fetchQuery({
      lineNumber,
      deviceOfferId,
      ifpOfferId: ifpId,
      cartId: cartId || null,
    }).then(
      ({
        me: {
          line: {
            planProductOffers: { offerDetails },
          },
        },
      }) => {
        if (offerDetails) {
          setOfferDetails(offerDetails);
        }
        setLoader(false);
      },
    );
  };

  const handleExistingPlanSelection = async (
    planKey: string,
    offerName: string,
  ) => {
    trackCustomLink(offerName);
    const lineNumber = getLineNumber(planKey);
    const isBuyNewSparkPlan = lineNumber === PlanBundleOptions.ChooseNewPlan;
    setSelectedLineNumber(planKey);
    setOfferDetails(null);
    const unSupportedLinesForChangePlan = getUnsupportedLinesForChangePlan({
      ineligibleLines,
      planKey: selectedLineNumber,
    });
    const shouldRetrievePlanOfferDetails =
      !isBuyNewSparkPlan && !unSupportedLinesForChangePlan.length;
    if (shouldRetrievePlanOfferDetails) {
      await getUpdatedPlanOfferDetails(lineNumber);
    }
  };

  function handleChangeAccount() {
    dispatch({
      type: ProductDetailsActionTypes.SetIfpEligibility,
      ifpEligibility: {
        activeStep: IfpEligibilityCheckSteps.AccountSelection,
      },
    });
    trackCustomLink('Change account');
  }

  const lines: MergedLines = [
    ...eligibleLines.map((line) => ({ ...line, isEligible: true })),
    ...ineligibleLines.map((line) => ({ ...line, isEligible: false })),
  ];

  const hasNoLines = lines.length === 0;

  const isNewPlanChecked =
    selectedLineNumber === PlanBundleOptions.ChooseNewPlan;

  const linkRegionId = isNewPlanChecked
    ? 'productDetails.line.buyAPlan'
    : 'productDetails.line.yourPlans';

  const isAccessory = UNSUPPORTED_CATEGORIES_FOR_BUY_NEW_PLAN.includes(
    selectedDevice?.dataLayerCategory,
  );

  if (hasNoLines) {
    return <IneligibleWarning hasLines={false} />;
  }

  return (
    <LinkRegion regionId={linkRegionId}>
      <ChangeAccountButton
        accountName={lines[0].accountName}
        accountNumber={lines[0].accountNumber}
        onChangeAccount={handleChangeAccount}
        numberOfAccounts={accounts.length}
      />

      <Box mb="s3">
        <ChoiceboxGroup title="Your plans">
          {lines.map(
            ({ lineNumber, offerId, offerName, isEligible, reason }) => {
              const planKey = `${lineNumber}-${offerId}`;
              const isChecked = planKey === selectedLineNumber;
              const isIneligibleLine =
                planKey === selectedLineNumber && !isEligible;

              return (
                <Fragment key={planKey}>
                  <ChoiceBox
                    name={offerName}
                    value={planKey}
                    checked={isChecked}
                    onChange={() => {
                      handleExistingPlanSelection(planKey, offerName);
                    }}
                  >
                    <Box>
                      <LinkRegion isNotTracked>
                        <Text variant="bodySDemi">{lineNumber}</Text>
                      </LinkRegion>
                      <Text variant="bodySMedium">{offerName}</Text>
                    </Box>
                  </ChoiceBox>
                  {isIneligibleLine ? (
                    <LinkRegion regionId="productDetails.chooseAPlan">
                      <IneligibleWarning reason={reason} hasLines />
                    </LinkRegion>
                  ) : null}
                </Fragment>
              );
            },
          )}
          {!isAccessory ? (
            <ChoiceBox
              name="newPlan"
              value={PlanBundleOptions.ChooseNewPlan}
              checked={isNewPlanChecked}
              onChange={() => {
                handleExistingPlanSelection(
                  PlanBundleOptions.ChooseNewPlan,
                  BUY_NEW_SPARK_PLAN,
                );
              }}
            >
              <Text variant="bodySDemi">{BUY_NEW_SPARK_PLAN}</Text>
            </ChoiceBox>
          ) : null}
        </ChoiceboxGroup>
      </Box>
    </LinkRegion>
  );
};

export default ExistingPlans;
